// ============================================================================
// QuantumAI Shield — Account Status Utility
// Single source of truth for "what's this user's billing state right now?"
//
// Status precedence (top wins):
//   1. paid       — paid_until is in the future
//   2. expired    — trial ended, no active paid_until
//   3. warning    — trial active, ≤7 days remaining
//   4. active     — trial active, >7 days remaining
//
// Optional app_metadata fields (all set via Supabase Admin API):
//   trial_started_at        — overrides created_at as trial anchor (admin reset)
//   extra_days              — bonus trial days (admin extension)
//   paid_until              — subscription end date (set by Stripe webhook or admin)
//   stripe_customer_id      — Stripe customer reference (set by Stripe webhook)
//   stripe_subscription_id  — Stripe subscription reference (set by Stripe webhook)
// ============================================================================

export const TRIAL_DAYS            = 14;
export const WARNING_DAYS          = 7;   // trial warning window
export const RENEWAL_WARNING_DAYS  = 14;  // paid-user renewal warning window

/**
 * Compute account status from a Supabase user object.
 *
 * @param {Object} user - Supabase user (from supabase.auth.getUser() or decoded JWT)
 * @returns {Object} { status, daysLeft, expiresAt, renewalSoon? }
 *     status: 'paid' | 'active' | 'warning' | 'expired'
 *     daysLeft: integer — days until trial ends (trial) or subscription renews (paid)
 *     expiresAt: Date — trial end or subscription renewal date
 *     renewalSoon: boolean (paid only) — true when within RENEWAL_WARNING_DAYS
 */
export function getAccountStatus(user) {
  if (!user || !user.created_at) {
    return { status: 'expired', daysLeft: 0, expiresAt: null };
  }

  // ── 1. PAID CHECK (takes precedence over everything else) ────────────────
  const paidUntilIso = user.app_metadata && user.app_metadata.paid_until;
  if (paidUntilIso) {
    const paidUntil = new Date(paidUntilIso);
    const msUntilRenewal = paidUntil.getTime() - Date.now();
    if (msUntilRenewal > 0) {
      const daysLeft = Math.max(0, Math.ceil(msUntilRenewal / 86_400_000));
      return {
        status: 'paid',
        daysLeft,
        expiresAt: paidUntil,
        renewalSoon: daysLeft <= RENEWAL_WARNING_DAYS,
      };
    }
    // paid_until is in the past — subscription ended, fall through to trial check
  }

  // ── 2. TRIAL CHECK ───────────────────────────────────────────────────────
  const anchorIso = user.app_metadata && user.app_metadata.trial_started_at;
  const anchorMs  = new Date(anchorIso || user.created_at).getTime();
  const extraDays = Number(user.app_metadata && user.app_metadata.extra_days) || 0;
  const totalDays = TRIAL_DAYS + extraDays;
  const expiresAt = new Date(anchorMs + totalDays * 86_400_000);
  const msLeft    = expiresAt.getTime() - Date.now();
  const daysLeft  = Math.max(0, Math.ceil(msLeft / 86_400_000));

  if (msLeft <= 0)              return { status: 'expired', daysLeft: 0, expiresAt };
  if (daysLeft <= WARNING_DAYS) return { status: 'warning', daysLeft,    expiresAt };
  return                              { status: 'active',  daysLeft,    expiresAt };
}

/** Quick boolean: should this user be allowed access right now? */
export function isAccessAllowed(user) {
  return getAccountStatus(user).status !== 'expired';
}

/** Human-readable message for UI banners. Returns null when no message needed. */
export function getStatusMessage(user) {
  const s = getAccountStatus(user);
  if (s.status === 'paid') {
    if (!s.renewalSoon) return null;
    const formatted = s.expiresAt.toLocaleDateString('en-US',
      { month: 'short', day: 'numeric', year: 'numeric' });
    return `Subscription renews on ${formatted}`;
  }
  if (s.status === 'active') return null;
  if (s.status === 'warning') {
    if (s.daysLeft === 0) return 'Your trial ends today.';
    if (s.daysLeft === 1) return 'Your trial ends tomorrow.';
    return `Your trial ends in ${s.daysLeft} days.`;
  }
  if (s.status === 'expired') return 'Your 14-day trial has ended.';
  return null;
}

// CommonJS fallback for Node.js servers that don't use ESM modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    TRIAL_DAYS, WARNING_DAYS, RENEWAL_WARNING_DAYS,
    getAccountStatus, isAccessAllowed, getStatusMessage,
  };
}
